import { useTranslation } from "react-i18next";
import type { OrganisationTrendMonthResponse, OrganisationTrendResponse } from "../../api/organisationReporting";
import { getVendorColor } from "../charts/chartColors";
import panelStyles from "../consumption/ConsumptionSummary.module.css";
import { formatRoundedMoney } from "../consumption/formatters";
import { VendorIcon } from "../consumption/VendorIcon";
import { sectionIds } from "../organisation/organisationInsights";
import { HoverNote } from "./HoverNote";
import { seatUse } from "./overviewFigures";
import styles from "./Overview.module.css";

/**
 * Given and used: for each tool, how many people had it in the month and how many of them used it, as a bar of the people with it
 * and the users filled in. The empty part is people given the tool who did not use it; where the tool is billed by the seat, what
 * those seats cost is a note on the count.
 *
 * @param props.trend - The trend, for the tools' names and order.
 * @param props.month - The month to show.
 */
export function SeatUsePanel({ trend, month }: { trend: OrganisationTrendResponse; month: OrganisationTrendMonthResponse }): React.JSX.Element {
  const { t } = useTranslation();
  const monthName = new Date(`${month.periodStart}T00:00:00Z`).toLocaleDateString("en-US", { month: "long", timeZone: "UTC" });
  const uses = seatUse(trend, month);
  const idleTotal = uses.reduce((sum, use) => sum + use.idleSpend, 0);

  return (
    // The "worth a look" list's unused seats open here for a viewer who may not open the people who hold them.
    <section id={sectionIds.seats} className={`${panelStyles.forecastPanel} ${styles.anchored}`} data-spotlight aria-labelledby="overview-seats-heading">
      <h2 id="overview-seats-heading" className={`panel-title ${panelStyles.panelTitle}`}>
        {t("overview.seats.title")}
      </h2>
      <p className={styles.subtitle}>{t("overview.seats.subtitle", { month: monthName })}</p>
      <ul className={styles.seatList}>
        {uses.map((use, index) => {
          const unused = Math.max(0, use.allocatedPeople - use.activePeople);
          const line =
            unused === 0 ? t("overview.seats.allUsed", { count: use.allocatedPeople }) : t("overview.seats.line", { active: use.activePeople, allocated: use.allocatedPeople });
          return (
            <li key={use.vendorId} className={styles.seat} style={{ "--provider-colour": getVendorColor(use.vendorId, index), "--row-index": index } as React.CSSProperties}>
              <span className={styles.seatIcon} aria-hidden="true">
                <VendorIcon vendorId={use.vendorId.toLowerCase()} />
              </span>
              <div className={styles.seatText}>
                <p className={styles.seatName}>
                  <span>{use.displayName}</span>
                  {use.usedPercent !== null && <span className={styles.seatPercent}>{`${String(Math.round(use.usedPercent))}%`}</span>}
                </p>
                {/* Decorative: the line beneath says the same in words. */}
                <span className={styles.seatBar} aria-hidden="true">
                  <span style={{ width: `${String(use.usedPercent ?? 100)}%` }} />
                </span>
                <p className={styles.seatLine}>
                  {line}
                  {unused > 0 && (
                    <>
                      <span aria-hidden="true">{" · "}</span>
                      {use.idleSpend > 0 ? (
                        <HoverNote className={styles.seatUnused} note={t("overview.seats.idleSpend", { amount: formatRoundedMoney(use.idleSpend, trend.currency) })}>
                          {t("overview.seats.unused", { count: unused })}
                        </HoverNote>
                      ) : (
                        <span className={styles.seatUnused}>{t("overview.seats.unused", { count: unused })}</span>
                      )}
                    </>
                  )}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
      {idleTotal > 0 && (
        <p className={styles.dataNote}>
          {t("overview.seats.idleTotal", { amount: formatRoundedMoney(idleTotal, trend.currency), month: monthName })}{" "}
          <span className={styles.noteAside}>{t("overview.seats.idleExplained")}</span>
        </p>
      )}
    </section>
  );
}
